// Public-link share modal (Phase 3.3 FE) — shows the minted token URL + QR.
//
// The plaintext token is shown ONCE (the backend never returns it again). The QR
// is rendered client-side via a dynamically-imported `qrcode` (no third-party
// service — the token must never leave the browser to an external host).

import { useEffect, useRef, useState } from 'react'
import { PexIcon } from './icons/PexIcon'
import { publicResolveUrl, type PublicTokenMint } from '../api/file-lifecycle'
import { useT } from '../i18n'
import { sharedUiI18n } from '../i18n/components/shared-ui'
import { formatDate } from '../lib/datetime'

export function ShareLinkModal({
  mint,
  filename,
  onClose,
}: {
  mint: PublicTokenMint
  filename: string
  onClose: () => void
}) {
  const t = useT(sharedUiI18n).shareLink
  const url = publicResolveUrl(mint.resolve_path)
  const [qr, setQr] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let alive = true
    import('qrcode')
      .then((m) => m.toDataURL(url, { width: 220, margin: 1 }))
      .then((dataUrl) => {
        if (alive) setQr(dataUrl)
      })
      .catch(() => {
        if (alive) setQr(null)
      })
    return () => {
      alive = false
    }
  }, [url])

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current)
    }
  }, [])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current)
      copiedTimerRef.current = setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard blocked — the URL is selectable below */
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{t.title}</h3>
            <p className="mt-0.5 truncate text-xs text-slate-500">{filename}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100"
            aria-label={t.close}
          >
            <PexIcon name="close" size="xs" tone="inherit" />
          </button>
        </div>

        <div className="mt-4 flex justify-center">
          {qr ? (
            <img
              src={qr}
              alt={t.qrAlt}
              className="rounded border border-slate-200"
              width={220}
              height={220}
            />
          ) : (
            <div className="flex h-[220px] w-[220px] items-center justify-center rounded border border-slate-200 text-xs text-slate-400">
              {t.generatingQr}
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <input
            readOnly
            value={url}
            className="min-w-0 flex-1 rounded border border-slate-200 px-2 py-1.5 font-mono text-[11px] text-slate-600"
            onFocus={(e) => e.target.select()}
          />
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1 rounded bg-indigo-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
          >
            {copied ? <PexIcon name="check" className="h-[12px] w-[12px]" size="xs" tone="inherit" /> : <PexIcon name="copy" className="h-[12px] w-[12px]" size="xs" tone="inherit" />}
            {copied ? t.copied : t.copy}
          </button>
        </div>

        <p className="mt-3 text-[11px] text-slate-400">
          {t.expires(formatDate(mint.expires_at, {}))}
          {mint.max_uses != null && t.uses(mint.max_uses)}
          {t.expiryTail}
        </p>
      </div>
    </div>
  )
}
